import React, { useState, useCallback, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-hot-toast";
import forceCommunityBanService from "../../api/services/forceCommunityBanService";
import { confirmWarning } from "../../utils/confirmDialog";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

export default function ForceBanUserModal({ isOpen, onClose, onSuccess }) {
  const [mode, setMode] = useState("id");
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setMode("id");
    setUserId("");
    setEmail("");
    setReason("");
  }, []);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const idTrim = userId.trim();
    const emailTrim = email.trim().toLowerCase();
    const reasonTrim = reason.trim();

    if (reasonTrim.length < 5) {
      toast.error("Reason must be at least 5 characters.");
      return;
    }
    if (mode === "id") {
      const n = Number(idTrim);
      if (!Number.isFinite(n) || n < 1) {
        toast.error("Enter a valid numeric user ID.");
        return;
      }
    } else if (!emailTrim) {
      toast.error("Enter the user’s email.");
      return;
    }

    const confirmMsg =
      mode === "id"
        ? `Restrict app user #${idTrim}? They will be blocked like a report-based ban and notified with your reason.`
        : `Restrict the user with email ${emailTrim}? They will be notified with your reason.`;

    const ok = await confirmWarning(confirmMsg, "Restrict this user?");
    if (!ok) return;

    setSubmitting(true);
    try {
      const body =
        mode === "id"
          ? { userId: idTrim, reason: reasonTrim }
          : { email: emailTrim, reason: reasonTrim };
      const data = await forceCommunityBanService.forceBan(body);
      toast.success(data?.message || "User restricted successfully.");
      reset();
      onClose();
      await onSuccess?.();
    } catch (err) {
      const msg =
        (err && typeof err === "object" && err.message) ||
        err?.response?.data?.message ||
        (typeof err === "string" ? err : null) ||
        "Request failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="force-ban-title">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 id="force-ban-title" className="text-lg font-bold text-gray-900">
            Force community ban
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
            aria-label="Close">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <p className="text-xs text-gray-600">
            Same <strong>permanent restriction</strong> as automated report strikes. Reason is sent in-app, by push
            (if enabled), and by email. Staff accounts cannot be banned.
          </p>

          <div>
            <span className={labelClass}>Identify user</span>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="fcb-mode" checked={mode === "id"} onChange={() => setMode("id")} />
                User ID
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="fcb-mode" checked={mode === "email"} onChange={() => setMode("email")} />
                Email
              </label>
            </div>
          </div>

          {mode === "id" ? (
            <div>
              <label className={labelClass} htmlFor="fcb-modal-userid">
                App user ID
              </label>
              <input
                id="fcb-modal-userid"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="e.g. 12345"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className={inputClass}
              />
            </div>
          ) : (
            <div>
              <label className={labelClass} htmlFor="fcb-modal-email">
                Email
              </label>
              <input
                id="fcb-modal-email"
                type="email"
                autoComplete="off"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
          )}

          <div>
            <label className={labelClass} htmlFor="fcb-modal-reason">
              Reason (shown to the user)
            </label>
            <textarea
              id="fcb-modal-reason"
              rows={4}
              maxLength={2000}
              placeholder="Explain why their account is restricted."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputClass}
              required
            />
            <p className="text-[11px] text-gray-500 mt-1">{reason.trim().length} / 2000 — minimum 5 characters</p>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
              {submitting ? "Applying…" : "Restrict user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
